import { CSSProperties, ReactNode, memo, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { FloatingPortal } from '@floating-ui/react';
import { motion, AnimatePresence, MotionProps } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { NakedUl, Text } from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { Label } from '../types';

export type TooltipData = {
  color: string;
  formattedValue: number | string;
  groupId?: string;
  id: string;
  label: Label;
  subLabel?: string;
  value?: number;
};

export type CustomTooltipTitle = {
  groupName: string;
  hoveredSegment?: string | null;
};

export type TooltipInterfaceProps = {
  bottomContentRenderer?: (values: Record<string, number>) => ReactNode;
  clickHelperText?: string;
  data: TooltipData[];
  isClickable?: boolean;
  itemType?: 'bar' | 'segment';
  labelsTransform?: CSSProperties['textTransform'];
  name?: string | ReactNode;
  noContainer?: boolean;
  skipActiveLegendsCheck?: boolean;
} & TooltipContainerProps;

type TooltipContainerProps = {
  xPos?: number;
  yPos?: number;
  applyMargin?: boolean;
} & MotionProps;

const pagePadding = 24;
const xMargin = 16;

export const TooltipContainer = ({
  applyMargin,
  xPos = 0,
  yPos = 0,
  children,
  ...motionProps
}: TooltipContainerProps & { children: ReactNode }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [x, setX] = useState<number | undefined>();
  const [y, setY] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (!applyMargin && (!xPos || !yPos)) {
      return;
    }

    const height = contentRef.current?.clientHeight || 0;
    const width = contentRef.current?.clientWidth || 0;

    const rightSpace = window.innerWidth - (xPos + xMargin) - pagePadding;
    const bottomSpace = window.innerHeight - yPos - pagePadding * 2;

    const x = rightSpace > width ? xPos + xMargin : xPos - width - xMargin;

    setX(x < 0 ? xMargin : x);
    setY(bottomSpace > height / 2 ? yPos - height / 2 : yPos - height);
  }, [applyMargin, xPos, yPos]);

  if (x === undefined || y === undefined) {
    return null;
  }

  return (
    <FloatingPortal>
      <AnimatePresence>
        <motion.div
          css={styles.wrapper}
          ref={contentRef}
          style={{
            left: x,
            top: y,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            delay: 0.1,
          }}
          {...motionProps}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </FloatingPortal>
  );
};

export const Tooltip = memo(
  ({
    bottomContentRenderer,
    clickHelperText,
    data = [],
    isClickable,
    itemType,
    labelsTransform,
    name,
    noContainer = false,
    skipActiveLegendsCheck,
    ...rest
  }: TooltipInterfaceProps) => {
    const { t } = useTranslation('analytics');
    const { activeLegends, labels } = useChartContext();

    const content = (
      <NakedUl css={styles.list}>
        {name && <li className='tip-name'>{typeof name === 'string' ? labels[name] || name : name}</li>}
        {data.map(
          ({ color, formattedValue, id, label, subLabel }, idx) =>
            (skipActiveLegendsCheck || activeLegends.includes(id)) && (
              <li className='chart-tooltip-list' key={onlyText(label) || `${id}-${idx}`}>
                <div className='tooltip-main'>
                  <Text as='span' weight='bold' size='medium' style={{ color }}>
                    {formattedValue}
                  </Text>
                  <Text
                    as='span'
                    size='medium'
                    style={{
                      color: label ? 'inherit' : theme.colors.status.critical,
                      textTransform: labelsTransform,
                    }}
                  >
                    {label || id}
                  </Text>
                </div>
                {subLabel && (
                  <Text as='span' size='small' color='subdued'>
                    {subLabel}
                  </Text>
                )}
              </li>
            )
        )}
        {bottomContentRenderer && (
          <li className='chart-tooltip-list' key='bottom-content'>
            {bottomContentRenderer(data.reduce((acc, { id, value = 0 }) => ({ ...acc, [id]: value }), {}))}
          </li>
        )}
        {isClickable && (
          <li className='chart-tooltip-list' key='clickable' style={{ minWidth: '150px' }}>
            <Text as='i' color='subdued' size='small'>
              {clickHelperText ??
                (itemType ? t('Click the {{itemType}} to learn more', { itemType }) : t('Click to learn more'))}
            </Text>
          </li>
        )}
      </NakedUl>
    );

    return noContainer ? content : <TooltipContainer {...rest}>{content}</TooltipContainer>;
  }
);

Tooltip.displayName = 'Tooltip';

const styles = {
  wrapper: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    max-width: 90vw;
    overflow: hidden;
    pointer-events: none;
    position: fixed;
    z-index: ${theme.zIndex.modals + 1}; // set to place above modals
  `,

  list: css`
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    min-width: ${theme.spacing(12.5)};
    padding: ${theme.spacing(2, 2, 1.5, 2)};

    .tip-name {
      background-color: ${theme.colors.white};
      border-bottom: 1px solid ${theme.colors.neutral5};
      color: ${theme.colors.neutral40};
      font-size: ${theme.font.size.small};
      padding: ${theme.spacing(0, 0, 1)};
      white-space: nowrap;
    }

    .chart-tooltip-list {
      border-bottom: 1px solid ${theme.colors.neutral5};
      padding: ${theme.spacing(0.5, 0)};

      .tooltip-main {
        align-items: center;
        color: ${theme.colors.neutral70};
        display: flex;
        flex-wrap: no-wrap;
        gap: ${theme.spacing(1)};

        > span {
          white-space: nowrap;
        }
      }

      &:last-of-type {
        border-bottom: none;
      }
    }
  `,
};
